import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { resolveDock } from "@/lib/docking";
import type { DockableModule } from "@/lib/docking";

interface DockedModuleProps {
  module: DockableModule;
  /** The tab from the URL, so a deep link lands inside the surface, not on its home page. */
  tabKey?: string | null;
  /**
   * A per-visit deep path inside the surface (`?at=`), for the part of a link
   * no route can express — which artefact, which record. Untrusted (it comes
   * from the URL bar); `resolveDock` ignores any value that leaves the origin.
   */
  at?: string | null;
  /** Who to name when the surface cannot be docked. Never inferred — from the registry. */
  ownerName?: string | null;
  /**
   * Frame height, for a caller that renders this OUTSIDE the shell's fill
   * chain. Left unset the frame takes all the height the shell hands it, which
   * is what the portal wants — see the note on the component.
   */
  minHeight?: string;
}

/**
 * C11 — a not-yet-rewritten surface, rendered inside the module viewport
 * (seam S6, decision D-2).
 *
 * Four things are deliberate here.
 *
 * **The visible "older surface" badge was removed** (operator decision, Damien
 * 2026-08-24: "remove these in each area, it's not the design"). BR-10's intent
 * was that a docked surface must not be passed off as native; in practice every
 * module in the portal is docked today, so the badge labelled everything and
 * distinguished nothing, while adding a row of chrome above every frame.
 * Be clear about what this costs: `DOCKED_LABEL` is still exported and still
 * returned by `resolveDock`, but it is no longer surfaced anywhere in the UI —
 * the iframe's accessible `title` is the module NAME, not the label. So the
 * "this is an older surface" disclosure is gone for sighted and screen-reader
 * users alike, not merely relocated. That is acceptable while every module is
 * docked and the statement carries no information. Reinstate the badge the day
 * a NATIVE module ships alongside a docked one, because the distinction starts
 * to mean something again at exactly that moment.
 *
 * **It is never half-docked.** A surface that cannot satisfy the contract
 * renders the reason, by name, in the same frame — not a blank region. See
 * `resolveDock`.
 *
 * **There is no `sandbox` attribute, on purpose.** Sandboxing without
 * `allow-same-origin` would strip the framed surface of the very cookie that
 * makes docking a single sign-in, and sandboxing WITH `allow-same-origin`
 * grants back everything it took away — the attribute would be theatre. The
 * boundary that actually holds is the pair the docking contract requires: the
 * surface sits on the one Access application (checked here and at
 * registration), and the surface itself sends
 * `Content-Security-Policy: frame-ancestors https://os.ketosource.co`, which
 * only it can assert. `referrerPolicy` is tightened to the origin so a deep
 * link inside the portal is not leaked as a full URL to the framed surface.
 *
 * **`allow="clipboard-write"` is delegated on purpose.** Permissions Policy
 * defaults `clipboard-write` to `self`, so `navigator.clipboard.writeText()`
 * REJECTS inside a cross-origin frame unless the embedder hands the permission
 * down — and every docked surface is cross-origin by construction. Without this
 * one attribute, any "copy this link / copy this value" control a surface ships
 * works when the surface is opened directly and silently fails behind the one
 * door, which is the only place people actually use it (canvas Share,
 * KET-4456). It delegates to the framed origin only, grants no read of what is
 * already on the clipboard, and still needs a user gesture.
 *
 * **The frame takes the height it is given, it does not pick one.** It used to
 * default to `70vh`, which meant the surface scrolled inside a box three
 * tenths shorter than the window while the page scrolled around it — two
 * scrollbars, and visible dead space under the frame (Damien, 2026-08-27).
 * The shell now passes height down (`fill`) from `h-dvh` through the viewport
 * and tab panel, and the frame simply fills it. `minHeight` survives for a
 * caller rendering this outside that chain, and is unset by default: a
 * `min-height` alongside `h-full` would re-impose the floor this removed.
 */
export function DockedModule({
  module,
  tabKey = null,
  at = null,
  ownerName = null,
  minHeight,
}: DockedModuleProps) {
  const dock = resolveDock(module, tabKey, at);

  if (dock.kind === "refused") {
    return (
      <Alert variant="destructive" className="max-w-2xl">
        <AlertTitle>{dock.message}</AlertTitle>
        <AlertDescription>
          {ownerName ? `${ownerName} can fix this.` : "Damien is the contact."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <iframe
        // Keyed on the resolved src so React REMOUNTS the element when you move
        // between two modules that frame the same origin. Without it React
        // reuses one iframe node and only swaps the `src` attribute, and the
        // browser keeps showing the document already loaded — the four Meteoric
        // rail items all rendered whichever Meteoric page opened first
        // (KET-3241). A remount is the only reliable reload.
        key={dock.src}
        title={dock.title}
        src={dock.src}
        className="min-h-0 w-full flex-1 border-0 bg-ks-surface"
        style={minHeight ? { minHeight } : undefined}
        referrerPolicy="strict-origin"
        // See the note on the component: without this, every docked surface's
        // copy-to-clipboard control fails inside the frame and works outside it.
        allow="clipboard-write"
        // NOT lazy: the frame now fills the viewport, so it is always in view
        // and `lazy` bought nothing while adding a decode/load delay on every
        // tab change (each tab remounts the element — see the `key` above).
        loading="eager"
      />
    </div>
  );
}
